﻿import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, GitCompare, Loader2, Search } from "lucide-react";
import type {
  BusinessTypeKey,
  FeatureFlagKey,
  PresetCatalog,
  PresetLevel,
} from "../../api/types";
import { INDUSTRY_ICONS } from "./industryIcons";
import { LevelCard } from "./LevelCard";
import { PresetCard } from "./PresetCard";

// Mirrors backend `_FEATURE_DEFAULTS_BY_TYPE` in
// `backend/custom_fields/preset_data.py`. Drives the "Tabs you'll see"
// chip strip preview before the preset is actually applied.
const FEATURE_DEFAULTS_BY_TYPE: Partial<
  Record<BusinessTypeKey, Partial<Record<FeatureFlagKey, boolean>>>
> = {
  consulting: { inventory_enabled: false, suppliers_enabled: false },
  tutoring:   { inventory_enabled: false, suppliers_enabled: false },
  gym:        { inventory_enabled: false },
};

const TAB_FLAG_BY_KEY: Record<string, FeatureFlagKey | null> = {
  overview: null,
  sales: null,
  expenses: null,
  customers: null,
  inventory: "inventory_enabled",
  suppliers: "suppliers_enabled",
  catalog: "catalog_enabled",
  calendar: "calendar_enabled",
  team: "employees_enabled",
};

const TAB_PREVIEW_KEYS = [
  "overview",
  "sales",
  "expenses",
  "customers",
  "inventory",
  "suppliers",
  "catalog",
  "calendar",
  "team",
] as const;

interface Props {
  catalog: PresetCatalog;
  /** Currently-applied preset on this business (null if none applied). */
  activeBusinessType?: BusinessTypeKey | null;
  activeLevel?: PresetLevel | null;
  onPick: (businessType: BusinessTypeKey, level: PresetLevel) => void;
  onOpenCompare?: () => void;
  /** Optional loading state while a preset apply mutation is in flight. */
  isApplying?: boolean;
}

/**
 * Two-screen composite picker:
 *   Screen 1 (list)   — grid of business-type cards; explicit "Choose" button on each.
 *   Screen 2 (levels) — back arrow + 3 side-by-side LevelCards for the chosen type.
 *
 * The "Compare presets" button remains on screen 1 (cross-business-type compare).
 * Picking a level on screen 2 triggers the parent's `onPick`, which in
 * FormFieldsTab opens the existing apply-confirm modal.
 */
export function PresetPicker({
  catalog,
  activeBusinessType,
  activeLevel,
  onPick,
  onOpenCompare,
  isApplying,
}: Props) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [chosenType, setChosenType] = useState<BusinessTypeKey | null>(null);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return catalog.business_types;
    return catalog.business_types.filter((bt) => {
      const name = t(`settings.formPresets.industry.${bt.key}.name` as never) as string;
      return (
        bt.key.toLowerCase().includes(needle) ||
        name.toLowerCase().includes(needle)
      );
    });
  }, [catalog.business_types, search, t]);

  // Field count for the Full level per business type — shown as a quick
  // "up to N fields" hint on the screen-1 card.
  const fullCount = useMemo(() => {
    const out: Partial<Record<BusinessTypeKey, number>> = {};
    for (const [bt, levels] of Object.entries(catalog.presets) as [
      BusinessTypeKey,
      Record<PresetLevel, { entities: Record<string, { builtin_visible: string[]; customs: unknown[] }> }>,
    ][]) {
      const full = levels.full;
      if (!full) continue;
      let n = 0;
      for (const entityCfg of Object.values(full.entities)) {
        n += entityCfg.builtin_visible.length + entityCfg.customs.length;
      }
      out[bt] = n;
    }
    return out;
  }, [catalog.presets]);

  // ── Screen 2: level picker for chosenType ───────────────────────────────
  if (chosenType) {
    const typeName = t(`settings.formPresets.industry.${chosenType}.name` as never) as string;
    const levelsForType = catalog.presets[chosenType];
    const Icon = INDUSTRY_ICONS[chosenType];
    const isActiveType = chosenType === activeBusinessType;

    return (
      <section data-testid="preset-picker-levels" className="space-y-6">
        <header className="flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={() => setChosenType(null)}
            data-testid="preset-picker-back"
            className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 -ml-2 text-sm font-medium text-fg-secondary hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("settings.formPresets.backToTypes")}
          </button>
          {isApplying && (
            <span className="inline-flex items-center gap-1.5 text-sm text-accent-700 dark:text-accent-200">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t("common.loading")}…
            </span>
          )}
        </header>

        <div className="flex items-start gap-3">
          <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg bg-accent-50 text-accent-700 dark:bg-accent-900/30 dark:text-accent-300">
            <Icon className="h-5 w-5" strokeWidth={2} />
          </span>
          <div>
            <h2 className="text-xl font-semibold text-fg-primary">
              {t("settings.formPresets.levelHeading", { type: typeName })}
            </h2>
            <p className="text-sm text-fg-secondary">
              {t("settings.formPresets.levelSubtitle")}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {catalog.levels.map((lvl) => (
            <LevelCard
              key={lvl}
              level={lvl}
              preset={levelsForType[lvl]}
              active={isActiveType && lvl === activeLevel}
              onPick={(picked) => onPick(chosenType, picked)}
            />
          ))}
        </div>

        {/* Tabs you'll see — preview of which dashboard tabs the chosen
            business type will turn on. Reflects the feature defaults from
            backend `_FEATURE_DEFAULTS_BY_TYPE` (mirrored above). */}
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-fg-secondary">
            {t("settings.formPresets.tabsYoullSee")}
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {TAB_PREVIEW_KEYS.map((tabKey) => {
              const flag = TAB_FLAG_BY_KEY[tabKey];
              const enabled =
                flag === null ||
                FEATURE_DEFAULTS_BY_TYPE[chosenType]?.[flag] !== false;
              if (!enabled) return null;
              return (
                <span
                  key={tabKey}
                  className="inline-flex items-center rounded-full bg-white dark:bg-slate-700 px-2.5 py-1 text-xs text-fg-primary ring-1 ring-slate-200 dark:ring-slate-600"
                >
                  {t(`settings.formPresets.tabPreview.${tabKey}` as never)}
                </span>
              );
            })}
          </div>
        </div>
      </section>
    );
  }

  // ── Screen 1: business type picker ──────────────────────────────────────
  return (
    <section data-testid="preset-picker" className="space-y-6">
      <header className="space-y-2">
        <h2 className="text-xl font-semibold text-fg-primary">
          {t("settings.formPresets.sectionHeading")}
        </h2>
        <p className="text-sm text-fg-secondary">
          {t("settings.formPresets.sectionSubtitle")}
        </p>
      </header>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative max-w-sm flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("settings.formPresets.searchPlaceholder")}
            data-testid="preset-search"
            className="block w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-fg-primary placeholder-slate-400 dark:placeholder-slate-500 pl-9 pr-3 py-2 text-sm shadow-sm focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500/20"
          />
        </div>
        {onOpenCompare && (
          <button
            type="button"
            onClick={onOpenCompare}
            data-testid="open-compare"
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-600 px-3 py-2 text-sm font-medium text-fg-primary hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
          >
            <GitCompare className="h-4 w-4" />
            {t("settings.formPresets.comparePresets")}
          </button>
        )}
      </div>

      {isApplying && (
        <div className="flex items-center gap-2 rounded-lg bg-accent-50 dark:bg-accent-900/30 px-3 py-2 text-sm text-accent-700 dark:text-accent-200">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t("common.loading")}…
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="preset-grid">
        {filtered.map((bt) => (
          <PresetCard
            key={bt.key}
            businessType={bt.key}
            active={bt.key === activeBusinessType}
            activeLabel={t("settings.formPresets.currentlyApplied")}
            detailedFieldCount={fullCount[bt.key]}
            onChoose={(picked) => setChosenType(picked)}
          />
        ))}
      </div>
    </section>
  );
}
